import {ConfigurationRules, FreePaidStatus} from './ConfigurationRules';
import {SectionSkeleton} from './SectionSkeleton';
import SectionConfiguration from './SectionConfiguration';
import {getFieldConfiguration, toSections} from '../../components/formConfiguration/Sections';
import FormView from './FormView';
import {LanguageId} from '../utils/Languages';
import FieldSkeleton from './IFieldSkeleton';
import {FieldId, SectionId} from './types';
import {EditingRights} from './EditingRights';

export interface IFormConfiguration {
  sections: SectionSkeleton[];
  instructions?: string;
  rules: ConfigurationRules;
  editingRights: EditingRights
}

export default class FormConfiguration {
  static initialize(data: Partial<IFormConfiguration>, configurations: SectionConfiguration[]): SectionSkeleton[] {
    return data.sections ||
      FormConfiguration.getTicketSection(configurations).concat(FormConfiguration.getEmptySections(configurations));
  }

  private static getTicketSection(configurations: SectionConfiguration[]): SectionSkeleton[] {
    return configurations.filter((section) => section.id === 'ticket').map((x) => x.toSkeleton());
  }

  private static getEmptySections(configurations: SectionConfiguration[]): SectionSkeleton[] {
    return configurations.filter((section) => section.id === 'general' || section.id === 'footer').map((x) => x.toSkeleton());
  }

  /**
   * Creates a new configuration which includes a new field
   */
  static addField(id: FieldId, sectionId: SectionId, sections: SectionSkeleton[], configurations: SectionConfiguration[]): SectionSkeleton[] {
    const fieldConfiguration = getFieldConfiguration(id, configurations);
    if (fieldConfiguration) {
      return sections.map((x) => {
        if (x.id === sectionId) {
          return {...x, fields: x.fields.concat([fieldConfiguration.toSkeleton()])};
        }
        return x;
      });
    }
    return sections;
  }

  /**
   * Add a new section to the form configuration
   */
  static addSection(id: SectionId, sections: SectionSkeleton[], configurations: SectionConfiguration[]): SectionSkeleton[] {
    if (configurations.find((x) => x.id === id)) {
      return FormConfiguration.moveFooterToBottom(sections.concat([{id, fields: []}]));
    }
    return sections;
  }

  static replaceFields(id: SectionId, fields: FieldSkeleton[], sections: SectionSkeleton[]): SectionSkeleton[] {
    return sections.map((section) => {
      if (section.id === id) {
        return {...section, fields};
      }
      return section;
    });
  }

  /**
   * Remove the section from configuration
   */
  static deleteSection(id: SectionId, sections: SectionSkeleton[]): SectionSkeleton[] {
    return sections.filter((section) => section.id !== id);
  }

  static setFreePaidStatus(status: FreePaidStatus, sections: SectionSkeleton[], configurations: SectionConfiguration[]): SectionSkeleton[] {
    if (status === 'OnlyFree') {
      return sections.filter((section) => section.id !== 'ticket');
    } else if (sections.findIndex((section) => section.id === 'ticket') === -1 && FormConfiguration.getTicketSection(configurations).length > 0) {
      return FormConfiguration.moveFooterToBottom(FormConfiguration.getTicketSection(configurations).concat(sections));
    }
    return sections;
  }

  protected static moveFooterToBottom(sections: SectionSkeleton[]): SectionSkeleton[] {
    const withoutFooter = sections.filter((section) => section.id !== 'footer');
    const footer = sections.filter((section) => section.id === 'footer');
    return withoutFooter.concat(footer);
  }

  static getView(
    activeLanguage: LanguageId, defaultLanguage: LanguageId,
    sections: SectionSkeleton[], configurations: SectionConfiguration[],
  ): FormView {
    const selectedSections = toSections(sections, activeLanguage, defaultLanguage, configurations);
    const allSections = configurations.map((x) => x.toView(activeLanguage, defaultLanguage));
    const fields = sections.map((x) => x.fields).flat(1).map((x) => x.id);
    const availableFields = configurations
      .map((section) => section.removeFields(fields))
      .filter((section) => section.fields.length > 0 ||
        selectedSections.map((x) => x.id).indexOf(section.id) < 0);
    return new FormView(selectedSections, allSections, availableFields);
  }
}

